import "./HardwarePage.css";
import { BasePageComponent } from "../../../shared/components/pages/BasePageComponent";
import { GpsTrackerResponse } from "../../../shared/response/hardware/GpsTrackerResponse";
import { GpsTrackerStatsResponse } from "../../../shared/response/hardware/GpsTrackerStatsResponse";
import { GpsTrackerService } from "../../services/hardware/GpsTrackerService";
import { GpsTrackerSaveService } from "../../services/hardware/GpsTrackerSaveService";
import { GpsTrackerDeleteService } from "../../services/hardware/GpsTrackerDeleteService";
import { GpsTrackerFormModal } from "./GpsTrackerFormModal";
import { GpsTrackerAsignarModal } from "./GpsTrackerAsignarModal";
import { GpsTrackerDescargaModal } from "./GpsTrackerDescargaModal";
import { ConfirmDialogComponent } from "../../../shared/components/ConfirmDialogComponent";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";
import { coincideBusqueda, palabrasBusqueda } from "../../../shared/utils/busquedaTexto";

// ── Estados derivados ────────────────────────────────────────────────────────
type TestState = "sin-testear" | "pasa" | "no-pasa";

function testState(d: GpsTrackerResponse): TestState {
  if (d.fechaTesteo == null) return "sin-testear";
  return d.pasaTest ? "pasa" : "no-pasa";
}

function fmtFecha(iso: string | null): string {
  if (!iso) return "—";
  const dt = new Date(iso);
  return dt.toLocaleDateString("es-CO", { year: "numeric", month: "short", day: "2-digit" });
}

export class HardwarePage extends BasePageComponent {
  private _all:      GpsTrackerResponse[] = [];
  private _filtered: GpsTrackerResponse[] = [];

  private _tbody!:  HTMLTableSectionElement;
  private _totEl!:  HTMLSpanElement;
  private _search!: HTMLInputElement;
  private _fTest!:  HTMLSelectElement;
  private _fAsig!:  HTMLSelectElement;

  public getAppRoute(): string { return "/sistema/hardware"; }
  public getPageTitle(): string | null { return "Hardware"; }

  protected override renderPageContent(pageContent: HTMLDivElement): void {
    pageContent.className = "sx-page hw-page";
    pageContent.innerHTML = `
      <div class="hw-content">

        <div class="hw-pg-hd">
          <div>
            <div class="hw-pg-ey">Sistema</div>
            <div class="hw-pg-ti">Hardware</div>
            <div class="hw-pg-su">Inventario · pruebas · asignación</div>
          </div>
          <button class="sx-btn sx-btn-primary sx-btn-sm" id="hw-nuevo-btn">
            <i class="ph ph-plus" aria-hidden="true"></i> Registrar dispositivo
          </button>
        </div>

        <div class="hw-kstrip">
          <div class="hw-kcard"><div class="hw-k-val" id="hw-k-total">—</div><div class="hw-k-lbl">Total dispositivos</div></div>
          <div class="hw-kcard"><div class="hw-k-val green" id="hw-k-asig">—</div><div class="hw-k-lbl">Asignados a cliente</div></div>
          <div class="hw-kcard"><div class="hw-k-val amber" id="hw-k-ofi">—</div><div class="hw-k-lbl">En oficina</div></div>
          <div class="hw-kcard"><div class="hw-k-val red" id="hw-k-notest">—</div><div class="hw-k-lbl">No pasan test</div></div>
        </div>

        <div class="hw-tabs">
          <div class="hw-tab on">GPS Tracker <span class="hw-tab-c" id="hw-tab-count">—</span></div>
          <div class="hw-tab hw-tab-disabled" title="Disponible próximamente">TARA</div>
        </div>

        <div class="hw-frow">
          <div class="hw-sw">
            <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
            <input class="hw-si" id="hw-search" placeholder="Buscar código o cliente…">
          </div>
          <select class="hw-fsel" id="hw-filter-test">
            <option value="">Todos los tests</option>
            <option value="pasa">Pasa test</option>
            <option value="no-pasa">No pasa test</option>
            <option value="sin-testear">Sin testear</option>
          </select>
          <select class="hw-fsel" id="hw-filter-asig">
            <option value="">Todos</option>
            <option value="cliente">Asignado a cliente</option>
            <option value="oficina">En oficina</option>
          </select>
          <span class="hw-ftot" id="hw-tot"></span>
        </div>

        <div class="hw-tbl-wrap">
          <table class="hw-tbl">
            <thead>
              <tr>
                <th>Código</th>
                <th>Testeado</th>
                <th>Fecha test</th>
                <th>Pasa test</th>
                <th>Asignado a</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody id="hw-tbody">
              <tr><td colspan="6" class="hw-empty">Cargando…</td></tr>
            </tbody>
          </table>
        </div>

      </div>
    `;

    this._tbody  = document.getElementById("hw-tbody")! as HTMLTableSectionElement;
    this._totEl  = document.getElementById("hw-tot")!;
    this._search = document.getElementById("hw-search")! as HTMLInputElement;
    this._fTest  = document.getElementById("hw-filter-test")! as HTMLSelectElement;
    this._fAsig  = document.getElementById("hw-filter-asig")! as HTMLSelectElement;

    document.getElementById("hw-nuevo-btn")!.onclick = () => this._onCreateClick();
    this._search.oninput = () => this._filterAndRender();
    this._fTest.onchange = () => this._filterAndRender();
    this._fAsig.onchange = () => this._filterAndRender();

    void this._loadData();
  }

  protected override beforeDispose(): void {}

  // ── Data ──────────────────────────────────────────────────────────────────
  private async _loadData(): Promise<void> {
    try {
      const [items] = await Promise.all([
        GpsTrackerService.instance.get(),
        this._loadStats(),
      ]);
      this._all = items.sort((a, b) => b.codigoInterno - a.codigoInterno);
      this._filterAndRender();
    } catch (error) {
      errorMessageComponent.addError("Error al cargar dispositivos", (error as Error).message);
      this._tbody.innerHTML = `<tr><td colspan="6" class="hw-empty">Error al cargar</td></tr>`;
    }
  }

  private async _loadStats(): Promise<void> {
    try {
      const s: GpsTrackerStatsResponse = await GpsTrackerService.instance.getStats();
      document.getElementById("hw-k-total")!.textContent  = String(s.total);
      document.getElementById("hw-k-asig")!.textContent   = String(s.asignadosACliente);
      document.getElementById("hw-k-ofi")!.textContent    = String(s.enOficina);
      document.getElementById("hw-k-notest")!.textContent = String(s.noPasanTest);
    } catch { /* KPIs quedan en "—" si falla */ }
  }

  // ── Filtro + render ────────────────────────────────────────────────────────
  /**
   * Filtra los GPS por lo escrito (códigos o nombre del cliente, con la regla común de `busquedaTexto`: todas las
   * palabras, sin distinguir mayúsculas ni tildes), por el testeo y por la asignación, y repinta.
   */
  private _filterAndRender(): void {
    const palabras = palabrasBusqueda(this._search.value);
    const ft = this._fTest.value;
    const fa = this._fAsig.value;

    this._filtered = this._all.filter((d) => {
      const matchQ = coincideBusqueda(palabras, d.codigoInterno, d.codigoCliente, d.clienteNombre);
      const matchT = !ft || testState(d) === ft;
      const matchA = !fa
        || (fa === "cliente" && d.clienteId != null)
        || (fa === "oficina" && d.clienteId == null);
      return matchQ && matchT && matchA;
    });

    document.getElementById("hw-tab-count")!.textContent = String(this._all.length);
    this._renderTable();
  }

  /**
   * Pinta la tabla de GPS filtrada con sus acciones: testear, asignar, descargar el CSV de un día (2026-10-29) y
   * eliminar.
   */
  private _renderTable(): void {
    const list = this._filtered;
    this._totEl.textContent = `${list.length} dispositivo${list.length === 1 ? "" : "s"}`;

    if (list.length === 0) {
      this._tbody.innerHTML = `<tr><td colspan="6" class="hw-empty">Sin resultados</td></tr>`;
      return;
    }

    this._tbody.innerHTML = list.map((d, i) => {
      const ts = testState(d);
      const testPill = ts === "sin-testear"
        ? `<span class="hw-pill p-am">No</span>`
        : `<span class="hw-pill p-ok">Sí</span>`;
      const pasaPill = ts === "sin-testear"
        ? `<span class="hw-pill p-na">—</span>`
        : ts === "pasa"
          ? `<span class="hw-pill p-ok"><span class="hw-dot"></span>Sí</span>`
          : `<span class="hw-pill p-er" title="${this._esc(d.notaTest ?? "")}"><span class="hw-dot"></span>No</span>`;
      const asigPill = d.clienteId != null
        ? `<span class="hw-pill p-bl">${this._esc(d.clienteNombre ?? "Cliente")}</span>`
        : `<span class="hw-pill p-na">Oficina</span>`;

      return `
        <tr>
          <td>
            <div class="hw-code">${d.codigoInterno}</div>
            <div class="hw-code-sub">${d.codigoCliente > 0 ? `N° cliente: ${d.codigoCliente}` : "Sin asignar"}</div>
          </td>
          <td>${testPill}</td>
          <td class="hw-fecha">${fmtFecha(d.fechaTesteo)}</td>
          <td>${pasaPill}</td>
          <td>${asigPill}</td>
          <td>
            <div class="hw-actions">
              <button class="sx-btn sx-btn-secondary sx-btn-sm" data-act="testear" data-idx="${i}">${ts === "sin-testear" ? "Testear" : "Editar test"}</button>
              <button class="sx-btn sx-btn-secondary sx-btn-sm" data-act="asignar" data-idx="${i}">Asignar</button>
              <button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" data-act="descargar" data-idx="${i}" title="Descargar CSV de un día" aria-label="Descargar CSV de un día"><i class="ph ph-download-simple"></i></button>
              <button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" data-act="eliminar" data-idx="${i}" title="Eliminar"><i class="ph ph-trash"></i></button>
            </div>
          </td>
        </tr>`;
    }).join("");

    this._tbody.querySelectorAll<HTMLButtonElement>("button[data-act]").forEach((btn) => {
      const d = list[Number(btn.dataset.idx)];
      btn.onclick = () => {
        if (btn.dataset.act === "testear")  this._onTestClick(d);
        if (btn.dataset.act === "asignar")  this._onAsignarClick(d);
        if (btn.dataset.act === "descargar") new GpsTrackerDescargaModal(d.codigoInterno).init();
        if (btn.dataset.act === "eliminar") this._onDeleteClick(d);
      };
    });
  }

  // ── Acciones ───────────────────────────────────────────────────────────────
  // Crear = registrar en oficina, sin testear. El backend asigna el código interno.
  private _onCreateClick(): void {
    new ConfirmDialogComponent({
      title: "Registrar nuevo dispositivo",
      message: "Se creará un GPS Tracker en oficina, sin testear. El código interno se asigna automáticamente. Podrás testearlo y asignarlo desde la tabla.",
      confirmLabel: "Registrar",
      onConfirm: async () => {
        try {
          const saved = await new GpsTrackerSaveService().save({
            id: null, fechaTesteo: null, pasaTest: false, notaTest: null,
          });
          this._all = [saved, ...this._all].sort((a, b) => b.codigoInterno - a.codigoInterno);
          this._filterAndRender();
          void this._loadStats();
          errorMessageComponent.addSuccess("Dispositivo registrado", `GPS Tracker #${saved.codigoInterno} creado en oficina.`);
        } catch (error) {
          errorMessageComponent.addError("Error al registrar", (error as Error).message);
        }
      },
    }).init();
  }

  // Testear = editar los campos de testeo del dispositivo (POST /save con id).
  private _onTestClick(item: GpsTrackerResponse): void {
    new GpsTrackerFormModal((saved) => {
      this._all = this._all.map((d) => (d.id === saved.id ? saved : d));
      this._filterAndRender();
      void this._loadStats();
    }, item).init();
  }

  private _onAsignarClick(item: GpsTrackerResponse): void {
    new GpsTrackerAsignarModal(item, (saved) => {
      this._all = this._all.map((d) => (d.id === saved.id ? saved : d));
      this._filterAndRender();
      void this._loadStats();
    }).init();
  }

  private _onDeleteClick(item: GpsTrackerResponse): void {
    new ConfirmDialogComponent({
      title: "Eliminar dispositivo",
      message: `¿Eliminar el GPS Tracker con código interno ${item.codigoInterno}?`,
      confirmLabel: "Eliminar",
      onConfirm: async () => {
        try {
          await new GpsTrackerDeleteService().delete(item.id);
          this._all = this._all.filter((d) => d.id !== item.id);
          this._filterAndRender();
          void this._loadStats();
        } catch (error) {
          errorMessageComponent.addError("Error al eliminar", (error as Error).message);
        }
      },
    }).init();
  }

  // ── Utils ──────────────────────────────────────────────────────────────────
  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }
}
